import { lazy, Suspense, useRef, useState } from 'react';
import { Box, CircularProgress, Fab, Paper, Tooltip } from '@mui/material';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';

const AssistantPanel = lazy(() => import('./AIAssistantPanel'));

/** One conversation across employer pages, kept mounted while minimized. */
export default function FloatingAssistant() {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const launcher = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
    launcher.current?.focus();
  };

  return (
    <>
      {opened && (
        <Paper
          id="jobvictory-ai-chat"
          role="dialog"
          aria-label="Chat with JobVictory AI"
          aria-modal={false}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { event.stopPropagation(); close(); }
          }}
          sx={{
            display: open ? 'flex' : 'none', position: 'fixed', flexDirection: 'column',
            zIndex: (theme) => theme.zIndex.drawer - 1,
            right: { xs: 12, sm: 24 }, bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
            width: { xs: 'calc(100% - 24px)', sm: 390 }, height: 580,
            maxHeight: 'calc(100dvh - 112px - env(safe-area-inset-bottom, 0px))',
            borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e8f3',
            boxShadow: '0 18px 70px rgba(19, 44, 86, 0.22)',
          }}
        >
          <Suspense fallback={<Box sx={{ m: 'auto' }}><CircularProgress size={24} aria-label="Loading chat" /></Box>}>
            <AssistantPanel open={open} onClose={close} />
          </Suspense>
        </Paper>
      )}
      <Tooltip title={open ? 'Minimize chat' : 'Chat with AI'} placement="left">
        <Fab
          ref={launcher}
          color="primary"
          aria-label={open ? 'Minimize AI chat' : 'Open AI chat'}
          aria-expanded={open}
          aria-controls={opened ? 'jobvictory-ai-chat' : undefined}
          aria-haspopup="dialog"
          onClick={() => { if (open) close(); else { setOpened(true); setOpen(true); } }}
          sx={{
            position: 'fixed', zIndex: (theme) => theme.zIndex.drawer - 1,
            right: { xs: 16, sm: 26 }, bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
            width: 58, height: 58, bgcolor: '#0875E9',
            boxShadow: '0 6px 24px rgba(8, 117, 233, 0.32)',
          }}
        >
          {open ? <KeyboardArrowDownRounded sx={{ fontSize: 30 }} /> : <ChatBubbleOutlineRounded sx={{ fontSize: 27 }} />}
          {!open && <Box component="span" sx={{ position: 'absolute', right: -2, top: -3, bgcolor: '#fff', color: '#0875E9', border: '2px solid #0875E9', borderRadius: 1, px: 0.5, fontSize: 9, fontWeight: 800, lineHeight: '16px' }}>AI</Box>}
        </Fab>
      </Tooltip>
    </>
  );
}
